import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
  Button,
  Badge,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getUserAnalytics, exportUserAnalytics } from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class UserAnalytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      analyticsData: null,
      breadcrumbItems: [
        { title: "Analytics", link: "/analytics" },
        { title: "User Analytics", link: "#" },
      ],
    };
  }

  getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  };

  getDefaultEndDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  componentDidMount() {
    this.loadAnalytics();
  }

  loadAnalytics = async () => {
    this.setState({ loading: true });
    const { startDate, endDate } = this.state;

    const response = await getUserAnalytics({ startDate, endDate });
    this.setState({
      analyticsData: response?.success ? response.data : null,
      loading: false,
    });
  };

  handleDateChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleApplyFilters = () => {
    this.loadAnalytics();
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const { startDate, endDate } = this.state;
      const res = await exportUserAnalytics({ startDate, endDate });
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  render() {
    const { loading, startDate, endDate, analyticsData } = this.state;

    const summary = analyticsData?.summary || {};
    const registrationTrend = analyticsData?.registrationTrend || [];
    const topUsers = analyticsData?.topUsers || [];
    const usersByActivity = analyticsData?.usersByActivity || {};

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="User Analytics" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        name="startDate"
                        value={startDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        name="endDate"
                        value={endDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <Button color="primary" onClick={this.handleApplyFilters}>
                      Apply
                    </Button>
                    <Button color="success" className="ml-2" disabled={this.state.exporting} onClick={this.handleExport}>
                      {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                    </Button>
                  </Col>
                  <Col md={4} className="text-right">
                    <Link to="/analytics" className="btn btn-outline-secondary">
                      Back to Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading user analytics...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <Row>
                  <Col md={3}>
                    <Card className="bg-primary text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Users</h6>
                        <h3>{this.formatNumber(summary.totalUsers)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-success text-white">
                      <CardBody>
                        <h6 className="text-white-50">New Users (Period)</h6>
                        <h3>{this.formatNumber(summary.newUsers)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-info text-white">
                      <CardBody>
                        <h6 className="text-white-50">Active Users</h6>
                        <h3>{this.formatNumber(summary.activeUsers)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-warning text-white">
                      <CardBody>
                        <h6 className="text-white-50">Growth Rate</h6>
                        <h3>
                          {summary.growthRate >= 0 ? "+" : ""}
                          {(summary.growthRate || 0).toFixed(1)}%
                        </h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  {/* Registration Trend */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Registration Trend</h4>
                        {registrationTrend.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <>
                            <Table className="table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th className="text-right">New Users</th>
                                  <th className="text-center">Trend</th>
                                </tr>
                              </thead>
                              <tbody>
                                {registrationTrend.slice(-10).map((item, index) => {
                                  const maxCount = Math.max(...registrationTrend.map(r => r.registrations || r.count || 0), 1);
                                  const itemCount = item.registrations || item.count || 0;
                                  const percentage = (itemCount / maxCount) * 100;
                                  return (
                                    <tr key={index}>
                                      <td>{item._id}</td>
                                      <td className="text-right">{this.formatNumber(itemCount)}</td>
                                      <td>
                                        <div className="progress" style={{ height: "8px" }}>
                                          <div
                                            className="progress-bar bg-success"
                                            style={{ width: `${percentage}%` }}
                                          ></div>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Top Users */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Top Users by Orders</h4>
                        {topUsers.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>User</th>
                                <th className="text-right">Orders</th>
                                <th className="text-right">Spent</th>
                              </tr>
                            </thead>
                            <tbody>
                              {topUsers.slice(0, 10).map((user, index) => (
                                <tr key={user._id || index}>
                                  <td>
                                    {index < 3 ? (
                                      <Badge color={index === 0 ? "warning" : index === 1 ? "secondary" : "dark"}>
                                        {index + 1}
                                      </Badge>
                                    ) : (
                                      index + 1
                                    )}
                                  </td>
                                  <td>
                                    <div>
                                      <strong>{user.name || "Unknown"}</strong>
                                    </div>
                                    <small className="text-muted">{user.email}</small>
                                  </td>
                                  <td className="text-right">{this.formatNumber(user.orders || user.orderCount)}</td>
                                  <td className="text-right">{this.formatCurrency(user.spending || user.totalSpent)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* User Activity Breakdown */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">User Activity Breakdown</h4>
                    <Row>
                      <Col md={3}>
                        <div className="text-center mb-3">
                          <div className="avatar-md mx-auto mb-2">
                            <span className="avatar-title rounded-circle bg-success-subtle text-success font-size-24">
                              <i className="fas fa-utensils"></i>
                            </span>
                          </div>
                          <h5>{this.formatNumber(usersByActivity.foodOrders)}</h5>
                          <p className="text-muted mb-0">Food Orders</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center mb-3">
                          <div className="avatar-md mx-auto mb-2">
                            <span className="avatar-title rounded-circle bg-warning-subtle text-warning font-size-24">
                              <i className="fas fa-bed"></i>
                            </span>
                          </div>
                          <h5>{this.formatNumber(usersByActivity.roomBookings)}</h5>
                          <p className="text-muted mb-0">Room Bookings</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center mb-3">
                          <div className="avatar-md mx-auto mb-2">
                            <span className="avatar-title rounded-circle bg-info-subtle text-info font-size-24">
                              <i className="fas fa-calendar-check"></i>
                            </span>
                          </div>
                          <h5>{this.formatNumber(usersByActivity.tableReservations)}</h5>
                          <p className="text-muted mb-0">Table Reservations</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center mb-3">
                          <div className="avatar-md mx-auto mb-2">
                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-24">
                              <i className="fas fa-car"></i>
                            </span>
                          </div>
                          <h5>{this.formatNumber(usersByActivity.transportRequests)}</h5>
                          <p className="text-muted mb-0">Transport Requests</p>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(UserAnalytics));
